<template>
  <div class="filterbar">
    <div class="filterbar-inner" id="filterbar-filters">
      <!-- View Switcher -->
      <div class="filterbar-inner__views">
        <button
          aria-label="Zu Tabellenansicht wechseln"
          :class="{ 'schedule-view-switch': true, active: scheduleView === 'table' }"
          @click="setScheduleView('table')"
        >
          <ui-icon icon="filmview-calendar-20"></ui-icon>
        </button>

        <button
          aria-label="Zu Kalenderansicht wechseln"
          :class="{ 'schedule-view-switch': true, active: scheduleView === 'calendar' }"
          @click="setScheduleView('calendar')"
        >
          <ui-icon icon="filmview-tile-20"></ui-icon>
        </button>
      </div>

      <!-- Date Filters -->
      <div class="collapse" id="date-collapse" data-bs-parent="#filterContainer">
        <div class="datefilters">
          <!-- Show placeholders while loading and no date filters available -->
          <template v-if="!hasFilterValues('date')">
            <div v-for="n in 5" :key="`date-placeholder-${n}`" class="d-flex align-items-center">
              <span
                class="placeholder placeholder-button"
                :style="`width: ${[70, 85, 60, 90][n - 1] || 75}px;`"
              ></span>
            </div>
          </template>

          <!-- Show actual date filters when available -->
          <template v-else>
            <div>
              <input
                :checked="activeDateFilter.length === 0"
                type="radio"
                value="alle"
                name="filterbar-filter-date"
                id="filterbar-filter-date-all"
                @change="setFilter('date')"
              />
              <label for="filterbar-filter-date-all">Alle</label>
            </div>
            <div v-for="(label, key) in getFilterValues('date')" :key="key">
              <input
                type="radio"
                :value="key"
                name="filterbar-filter-date"
                :id="`filterbar-filter-date-${sanitizeKeyForId(key)}`"
                :checked="activeDateFilter.includes(key)"
                @change="setFilter('date', key)"
              />
              <label :for="`filterbar-filter-date-${sanitizeKeyForId(key)}`">
                {{ label }}
              </label>
            </div>
          </template>
        </div>
      </div>

      <!-- Filter Buttons -->
      <div class="filterbar-inner__buttons">
        <button
          v-if="hasFilterValues('date')"
          class="btn btn-text collapsed"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#date-collapse"
          aria-expanded="false"
          aria-controls="date-collapse"
        >
          Zeitraum
          <ui-icon icon="chevron-20"></ui-icon>
        </button>

        <button
          class="btn btn-text collapsed btn-filters-collapse-mobile"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#filters-collapse"
          aria-expanded="false"
          aria-controls="filters-collapse"
          :disabled="!hasAnyFilters"
        >
          <span class="d-none d-sm-inline">Weiter</span> Filter
          <ui-icon icon="chevron-20"></ui-icon>
        </button>

        <button
          class="btn btn-outline collapsed btn-filters-collapse-desktop"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#filters-collapse"
          aria-expanded="false"
          aria-controls="filters-collapse"
          :disabled="!hasAnyFilters"
        >
          Filter
          <ui-icon icon="chevron-20"></ui-icon>
        </button>
      </div>

      <!-- Advanced Filters -->
      <div class="collapse" id="filters-collapse" data-bs-parent="#filterbar-filters">
        <div class="attributefilters">
          <!-- Auditorium Attributes -->
          <div
            class="attributefilters__row"
            v-if="hasFilterValues('auditorium') || hasFilterValues('release')"
          >
            <template v-if="hasFilterValues('auditorium')">
              <div v-for="(label, key) in getFilterValues('auditorium')" :key="key">
                <input
                  type="checkbox"
                  :value="key"
                  :id="`filterbar-filter-auditorium-${sanitizeKeyForId(key)}`"
                  :checked="getActiveFilterValues('auditorium').includes(key)"
                  @change="toggleFilter('auditorium', key)"
                />
                <label :for="`filterbar-filter-auditorium-${sanitizeKeyForId(key)}`">
                  {{ label }}
                </label>
              </div>
            </template>

            <!-- Release Versions -->
            <template v-if="hasFilterValues('release')">
              <div v-for="(label, key) in getFilterValues('release')" :key="key">
                <input
                  type="checkbox"
                  :value="key"
                  :id="`filterbar-filter-release-${sanitizeKeyForId(key)}`"
                  :checked="getActiveFilterValues('release').includes(key)"
                  @change="toggleFilter('release', key)"
                />
                <label :for="`filterbar-filter-release-${sanitizeKeyForId(key)}`">
                  {{ label }}
                </label>
              </div>
            </template>
          </div>

          <!-- FSK Ratings -->
          <div v-if="hasFilterValues('fsk')" class="attributefilters__row">
            <div v-for="(label, key) in getFilterValues('fsk')" :key="key">
              <input
                type="checkbox"
                :value="key"
                :id="`filterbar-filter-fsk-${sanitizeKeyForId(key)}`"
                :checked="getActiveFilterValues('fsk').includes(key)"
                @change="toggleFilter('fsk', key)"
              />
              <label :for="`filterbar-filter-fsk-${sanitizeKeyForId(key)}`">
                FSK {{ label }}
              </label>
            </div>
          </div>

          <!-- Performance Series -->
          <div v-if="hasFilterValues('series')" class="attributefilters__row">
            <div v-for="series in getFilterValues('series')" :key="series.slug">
              <input
                type="checkbox"
                :value="series.slug"
                :id="`filterbar-filter-series-${sanitizeKeyForId(series.slug)}`"
                :checked="getActiveFilterValues('series').includes(series.slug)"
                @change="toggleFilter('series', series.slug)"
              />
              <label
                :for="`filterbar-filter-series-${sanitizeKeyForId(series.slug)}`"
                v-html="series.name"
              >
              </label>
            </div>
          </div>

          <!-- Cinema/Site Filters -->
          <div v-if="hasFilterValues('cinema')" class="attributefilters__row">
            <div v-for="(label, key) in getFilterValues('cinema')" :key="key">
              <input
                type="checkbox"
                :value="key"
                :id="`filterbar-filter-cinema-${sanitizeKeyForId(key)}`"
                :checked="getActiveFilterValues('cinema').includes(key)"
                @change="toggleFilter('cinema', key)"
              />
              <label :for="`filterbar-filter-cinema-${sanitizeKeyForId(key)}`">
                {{ label }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from "vue";
import { useScheduleStore } from "../stores/scheduleStore.js";

// Use Pinia store
const scheduleStore = useScheduleStore();

// Use store state (Pinia stores are reactive)
const scheduleView = computed({
  get: () => scheduleStore.scheduleView,
  set: (value) => scheduleStore.setScheduleView(value),
});

const setScheduleView = (view) => {
  scheduleStore.setScheduleView(view);

  // Emit view change event for other components
  window.dispatchEvent(
    new CustomEvent("schedule-view-changed", {
      detail: { viewType: view },
    }),
  );
};

// Check if any filters are available (excluding date, which has its own button)
const hasAnyFilters = computed(() => {
  return (
    scheduleStore.hasFilterValues("auditorium") ||
    scheduleStore.hasFilterValues("release") ||
    scheduleStore.hasFilterValues("fsk") ||
    scheduleStore.hasFilterValues("series") ||
    scheduleStore.hasFilterValues("cinema")
  );
});

// Use store for active filters (Pinia getters are reactive)
const activeDateFilter = computed(() => scheduleStore.getActiveFilterValues("date"));
const activeAuditoriumFilters = computed(() => scheduleStore.getActiveFilterValues("auditorium"));
const activeReleaseFilters = computed(() => scheduleStore.getActiveFilterValues("release"));
const activeFskFilters = computed(() => scheduleStore.getActiveFilterValues("fsk"));
const activeSeriesFilters = computed(() => scheduleStore.getActiveFilterValues("series"));
const activeCinemaFilters = computed(() => scheduleStore.getActiveFilterValues("cinema"));

// Local filter configuration for UI (order and active state handled by store)
const filters = {
  series: {
    get filterValues() {
      return scheduleStore.getFilterValues("series") || [];
    },
    get active() {
      return activeSeriesFilters.value;
    },
  },
  date: {
    get filterValues() {
      return scheduleStore.getFilterValues("date") || [];
    },
    get active() {
      return activeDateFilter.value;
    },
  },
  release: {
    get filterValues() {
      return scheduleStore.getFilterValues("release") || [];
    },
    get active() {
      return activeReleaseFilters.value;
    },
  },
  cinema: {
    get filterValues() {
      return scheduleStore.getFilterValues("cinema") || [];
    },
    get active() {
      return activeCinemaFilters.value;
    },
  },
  auditorium: {
    get filterValues() {
      return scheduleStore.getFilterValues("auditorium") || [];
    },
    get active() {
      return activeAuditoriumFilters.value;
    },
  },
  fsk: {
    get filterValues() {
      return scheduleStore.getFilterValues("fsk") || [];
    },
    get active() {
      return activeFskFilters.value;
    },
  },
};

// Sync filters to URL and dispatch change event
const syncFiltersAndDispatch = () => {
  scheduleStore.syncFiltersToUrl();
  scheduleStore.dispatchFilterChange();
};

// Get filter values by key (using Pinia getter)
const getFilterValues = (filterKey) => {
  return scheduleStore.getFilterValues(filterKey) || [];
};

// Get active filter values by key (using Pinia getter)
const getActiveFilterValues = (filterKey) => {
  return scheduleStore.getActiveFilterValues(filterKey);
};

// Check if filter has values (using Pinia getter)
const hasFilterValues = (filterKey) => {
  return scheduleStore.hasFilterValues(filterKey);
};

/**
 * Sanitize a filter key for use in HTML ID attributes
 * Replaces spaces and commas with hyphens to ensure valid HTML IDs
 * @param {string} key - The filter key to sanitize
 * @returns {string} - Sanitized key safe for use in HTML IDs
 */
const sanitizeKeyForId = (key) => {
  if (!key || typeof key !== "string") {
    return "";
  }
  // Replace spaces and commas with hyphens, then remove any duplicate hyphens
  return key
    .replace(/[\s,]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

// Centralized filter toggle function
const toggleFilter = (filterType, filterValue) => {
  scheduleStore.toggleFilter(filterType, filterValue);
  syncFiltersAndDispatch();
};

const setFilter = (filterType, filterValue = null) => {
  if (!filterType) return;

  scheduleStore.setActiveFilter(filterType, filterValue ? [filterValue] : []);
  syncFiltersAndDispatch();
};

// Reset all filters
const resetAllFilters = () => {
  scheduleStore.resetFiltersAndUrl();
  scheduleStore.dispatchFilterChange();
};

// Handle reset event from MovieListing
const handleResetEvent = () => {
  resetAllFilters();
};

onMounted(() => {
  // Initialize from URL if present (using centralized store method)
  scheduleStore.initializeFiltersFromUrl();

  // Always dispatch filter change on mount (with or without URL filters)
  scheduleStore.dispatchFilterChange();

  // Start watching for URL hash changes (browser back/forward)
  scheduleStore.startWatchingHashChanges();

  // Listen for reset events
  window.addEventListener("vue-filterbar-reset", handleResetEvent);
});

onBeforeUnmount(() => {
  scheduleStore.stopWatchingHashChanges();
  window.removeEventListener("vue-filterbar-reset", handleResetEvent);
});
</script>
